import { Outlet, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { useCartStore } from '@/stores/cart.store';
import { useSettingsStore } from '@/stores/settings.store';
import { useEffect, useMemo } from 'react';
import { Header, Footer, type HeaderNavItem } from '@/components/ui';
import { CategoryStrip } from '@/components/home';
import { catalogService } from '@/services';
import { useLogout } from '@/hooks';

/** Temporal: poner en true para reactivar la barra de categorías bajo el header. */
const SHOW_CATEGORY_STRIP = false;

export function AppLayout(): JSX.Element {
  const { t } = useTranslation();
  const { t: tAuth } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuthStore();
  const { cart, fetchCart } = useCartStore();
  const initializeSettings = useSettingsStore((s) => s.initialize);
  const logoutMutation = useLogout();
  const isSeller = user?.role === 'SELLER' || user?.role === 'ADMINISTRATOR';
  const activeCategoryId = searchParams.get('category') ?? undefined;

  useEffect(() => {
    initializeSettings();
  }, [initializeSettings]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    }
  }, [isAuthenticated, fetchCart]);

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', 'visible'],
    queryFn: () => catalogService.getVisibleCategories(),
    staleTime: 60 * 1000,
    retry: 1,
    enabled: SHOW_CATEGORY_STRIP,
  });

  const cartItemCount = cart?.cartItems.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const navItems = useMemo(() => {
    const items: HeaderNavItem[] = [
      {
        to: '/products',
        label: t('nav.products'),
        slot: 'default',
        isActive: (pathname, search) =>
          pathname === '/products' && new URLSearchParams(search).get('type') !== 'tcg',
      },
      {
        to: '/products?type=tcg',
        label: t('nav.tcg'),
        slot: 'default',
        isActive: (pathname, search) =>
          (pathname === '/products' && new URLSearchParams(search).get('type') === 'tcg') ||
          pathname.startsWith('/yugioh') ||
          pathname.startsWith('/tcg'),
      },
    ];

    if (isAuthenticated) {
      items.push({ to: '/orders', label: t('nav.myOrders'), slot: 'default' });
      items.push({ to: '/cart', label: t('nav.cart'), showBadge: true, slot: 'cart' });
      if (isSeller) {
        items.push({
          to: '/seller/products',
          label: t('nav.myProducts'),
          slot: 'default',
        });
      }
      if (user?.role === 'SELLER') {
        items.push({ to: '/seller/orders', label: t('nav.mySales'), slot: 'default' });
      }
      if (user?.role === 'ADMINISTRATOR') {
        items.push({ to: '/admin/orders', label: t('adminOrders.navLink'), slot: 'default' });
      }
      items.push({ to: '/profile', label: t('nav.profile'), slot: 'profile' });
    } else {
      items.push({ to: '/login', label: t('nav.login'), slot: 'login' });
    }

    return items;
  }, [isAuthenticated, isSeller, t, user?.role]);

  const footerLinks = useMemo(
    () => [
      { to: '/', label: t('footer.home') },
      { to: '/products', label: t('footer.products') },
      {
        to: isAuthenticated ? '/profile' : '/login',
        label: isAuthenticated ? t('nav.profile') : t('footer.login'),
      },
    ],
    [isAuthenticated, t],
  );

  const year = new Date().getFullYear();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header
        appTitle={t('app.title')}
        navItems={navItems}
        cartCount={cartItemCount}
        isAuthenticated={isAuthenticated}
        searchPlaceholder={t('common.searchProductsPlaceholder')}
        searchAriaLabel={t('common.search')}
        menuOpenLabel={t('aria.openMenu')}
        menuCloseLabel={t('aria.closeMenu')}
        logoutLabel={tAuth('logout')}
        onLogout={() => logoutMutation.mutate()}
        logoutPending={logoutMutation.isPending}
      />
      {SHOW_CATEGORY_STRIP && categories.length > 0 ? (
        <CategoryStrip
          items={categories.map((c) => ({ id: c.id, name: c.name }))}
          activeId={activeCategoryId}
        />
      ) : null}
      <main className="w-full flex-1">
        <Outlet />
      </main>
      <Footer
        appTitle={t('app.title')}
        links={footerLinks}
        copyright={t('footer.copyright', { year })}
      />
    </div>
  );
}
